import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Box, IconButton,
  Menu, MenuItem, LinearProgress, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import YouTubeIcon from '@mui/icons-material/YouTube';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import AudioJobDetailsModal from './AudioJobDetailsModal';

const STATUS_MAP = {
  pending:     { label: 'Pending',     color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  icon: <ScheduleIcon sx={{ fontSize: 13 }} /> },
  extracting:  { label: 'Extracting',  color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: <MusicNoteIcon sx={{ fontSize: 13 }} /> },
  translating: { label: 'Translating', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  icon: <MusicNoteIcon sx={{ fontSize: 13 }} /> },
  completed:   { label: 'Completed',   color: '#22C55E', bg: 'rgba(34,197,94,0.12)',   icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
  failed:      { label: 'Failed',      color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   icon: <ErrorIcon sx={{ fontSize: 13 }} /> },
  cancelled:   { label: 'Cancelled',   color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.06)', icon: <CancelIcon sx={{ fontSize: 13 }} /> },
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

function AudioJobCard({ job, onDelete, onCancel, isAdmin = false }) {
  const [anchorEl, setAnchorEl]       = useState(null);
  const [audioUrl, setAudioUrl]       = useState(null);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const status = job.status?.toLowerCase() || 'pending';
  const s = STATUS_MAP[status] || STATUS_MAP.pending;

  const isProcessing = ['pending', 'extracting', 'translating'].includes(status);
  const isCompleted  = status === 'completed';
  const isFailed     = status === 'failed';

  const handleDownload = () => {
    if (!job.translated_audio_url) return;
    const a = document.createElement('a');
    a.href = job.translated_audio_url;
    a.download = `${job.video_title}_${job.target_language}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Download started');
  };

  const handleDelete = async () => {
    setDeleteOpen(false);
    try {
      await onDelete(job.id);
      toast.success('Job deleted');
    } catch { toast.error('Failed to delete job'); }
  };

  const handleCancel = async () => {
    setAnchorEl(null);
    try {
      await onCancel(job.id);
      toast.success('Job cancelled');
    } catch { toast.error('Failed to cancel job'); }
  };

  const getStepLabel = () => {
    if (status === 'extracting') return 'Extracting audio…';
    if (status === 'translating') return 'Translating…';
    if (status === 'pending') return 'Queued…';
    return '';
  };

  return (
    <>
      <Card sx={{
        backgroundColor: '#0A0A0A',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.2s ease, transform 0.2s ease',
        '&:hover': { borderColor: 'rgba(255,255,255,0.12)', transform: 'translateY(-1px)' },
      }}>
        <CardContent sx={{ p: 2.5, pb: '20px !important', display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* Top row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.75 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '8px',
              backgroundColor: 'rgba(255,255,255,0.05)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <MusicNoteIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.3)' }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px',
                color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', mb: 0.25,
              }}>
                {job.video_title || 'Untitled'}
              </Typography>

              {/* Source URL / type */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {job.video_source === 'upload' ? (
                  <VideoFileIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }} />
                ) : (
                  <YouTubeIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }} />
                )}
                <Typography sx={{
                  fontFamily: 'Inter, sans-serif', fontSize: '11px',
                  color: 'rgba(255,255,255,0.3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  maxWidth: 160,
                }}>
                  {job.video_source === 'upload' ? 'Uploaded video' : (job.youtube_url || 'YouTube')}
                </Typography>
              </Box>
            </Box>

            {/* Status badge */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.4, borderRadius: '5px',
              backgroundColor: s.bg, color: s.color,
              fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600,
              flexShrink: 0,
            }}>
              {s.icon}{s.label}
            </Box>

            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ color: 'rgba(255,255,255,0.25)', flexShrink: 0, p: 0.5,
                    '&:hover': { color: 'rgba(255,255,255,0.6)' } }}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Language row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LangBadge label="Source" value={job.source_language?.toUpperCase() || 'AUTO'} />
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.2)' }} />
            <LangBadge label="Target" value={job.target_language?.toUpperCase() || 'N/A'} />
            {job.video_duration > 0 && (
              <Box sx={{ ml: 'auto', fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                {formatDuration(job.video_duration)}
              </Box>
            )}
          </Box>

          {/* Progress bar */}
          {isProcessing && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                  {getStepLabel()}
                </Typography>
                {job.progress > 0 && (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 600, color: '#FFFFFF' }}>
                    {Math.round(job.progress)}%
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant={job.progress > 0 ? 'determinate' : 'indeterminate'}
                value={job.progress || 0}
                sx={{
                  height: 3, borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': { background: '#FFFFFF', borderRadius: 2 },
                }}
              />
            </Box>
          )}

          {/* Error */}
          {isFailed && job.error_message && (
            <Box sx={{
              mb: 2, px: 1.5, py: 1, borderRadius: '7px',
              backgroundColor: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.15)',
            }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#EF4444', lineHeight: 1.4 }}>
                {job.error_message}
              </Typography>
            </Box>
          )}

          {/* Action buttons */}
          {isCompleted && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <Button
                fullWidth
                startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
                onClick={() => setAudioUrl(job.translated_audio_url)}
                sx={{
                  backgroundColor: '#FFFFFF', color: '#000000',
                  fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px',
                  textTransform: 'none', borderRadius: '7px', py: 1,
                  '&:hover': { backgroundColor: '#F0F0F0' },
                }}
              >
                Play
              </Button>
              <Button
                fullWidth
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                onClick={handleDownload}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px',
                  textTransform: 'none', borderRadius: '7px', py: 1,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' },
                }}
              >
                Download
              </Button>
            </Box>
          )}

          {/* Date */}
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.22)', mt: 'auto', pt: 0.5 }}>
            {job.created_at ? format(new Date(job.created_at), 'MMM d, yyyy · HH:mm') : ''}
          </Typography>
        </CardContent>
      </Card>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.6)', minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => { setAnchorEl(null); setDetailsOpen(true); }}
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.7)', gap: 1.5,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}
        >
          <InfoIcon sx={{ fontSize: 16 }} /> View Details
        </MenuItem>
        {isProcessing && (
          <MenuItem
            onClick={handleCancel}
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#F59E0B', gap: 1.5,
                  '&:hover': { backgroundColor: 'rgba(245,158,11,0.08)' } }}
          >
            <CancelIcon sx={{ fontSize: 16 }} /> Cancel Job
          </MenuItem>
        )}
        {!isProcessing && (
          <MenuItem
            onClick={() => { setAnchorEl(null); setDeleteOpen(true); }}
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#EF4444', gap: 1.5,
                  '&:hover': { backgroundColor: 'rgba(239,68,68,0.08)' } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} /> Delete Job
          </MenuItem>
        )}
      </Menu>

      {/* Delete confirm */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{ sx: { backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#FFFFFF', fontSize: '16px' }}>
          Delete Audio Job?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            sx={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.5)', textTransform: 'none',
                  '&:hover': { color: '#FFFFFF', backgroundColor: 'transparent' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            sx={{
              backgroundColor: '#EF4444', color: '#FFFFFF', fontFamily: 'Inter, sans-serif',
              fontWeight: 600, textTransform: 'none', borderRadius: '7px', px: 2.5,
              '&:hover': { backgroundColor: '#DC2626' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audio player */}
      {audioUrl && (
        <Dialog
          open={Boolean(audioUrl)}
          onClose={() => setAudioUrl(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' } }}
        >
          <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#FFFFFF', fontSize: '16px' }}>
            Play Translated Audio
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <audio controls autoPlay style={{ width: '100%', marginTop: 8 }}>
              <source src={audioUrl} type="audio/mpeg" />
            </audio>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setAudioUrl(null)}
              sx={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.5)', textTransform: 'none',
                    '&:hover': { color: '#FFFFFF', backgroundColor: 'transparent' } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <AudioJobDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        job={job}
        isAdmin={isAdmin}
      />
    </>
  );
}

const LangBadge = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{label}</Typography>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>{value}</Typography>
  </Box>
);

export default AudioJobCard;
